import React from 'react';
import { Database, Users, Activity } from 'lucide-react';

const MeterRow = ({ title, icon: Icon, used, limit, suffix = '' }) => {
    // If limit is null/undefined or < 0, assume unlimited
    const isUnlimited = limit === null || limit === undefined || limit < 0;
    const percentage = isUnlimited ? 0 : Math.min(100, Math.round((used / limit) * 100));

    // Determine color based on usage percentage
    let trackColor = 'bg-blue-500';
    let trackGlow = 'shadow-[0_0_8px_rgba(59,130,246,0.3)]';
    let statusText = 'Good';
    let statusColor = 'text-green-400';

    if (!isUnlimited) {
        if (percentage >= 100) {
            trackColor = 'bg-red-500';
            trackGlow = 'shadow-[0_0_8px_rgba(239,68,68,0.4)]';
            statusText = 'Limit Reached';
            statusColor = 'text-red-400';
        } else if (percentage >= 80) {
            trackColor = 'bg-yellow-500';
            trackGlow = 'shadow-[0_0_8px_rgba(234,179,8,0.3)]';
            statusText = 'Near Limit';
            statusColor = 'text-yellow-400';
        }
    } else {
        statusText = 'Unlimited';
        statusColor = 'text-emerald-400';
        trackColor = 'bg-emerald-500';
        trackGlow = 'shadow-[0_0_8px_rgba(16,185,129,0.3)]';
    }

    return (
        <div className="py-3 first:pt-0 last:pb-0">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-gray-400" />
                    <h3 className="text-[#cccccc] text-xs font-semibold uppercase tracking-wider">{title}</h3>
                </div>
                <div className="flex items-baseline gap-1.5">
                    <span className="text-sm font-bold text-white">{used.toLocaleString()}</span>
                    {!isUnlimited && (
                        <span className="text-xs text-[#808080]">
                            / {limit.toLocaleString()} {suffix}
                        </span>
                    )}
                    {isUnlimited && suffix && (
                        <span className="text-xs text-[#808080]">{suffix}</span>
                    )}
                </div>
            </div>

            {/* Progress Bar Container */}
            <div className="relative h-1.5 w-full bg-[#1e1e1e] rounded-full overflow-hidden border border-[#3c3c3c]">
                <div
                    className={`absolute top-0 left-0 h-full rounded-full transition-all duration-1000 ease-out ${trackColor} ${!isUnlimited && percentage > 0 ? trackGlow : ''}`}
                    style={{ width: `${isUnlimited ? 100 : percentage}%` }}
                />
            </div>
        </div>
    );
};

export default function UsageMeters({ usage, loading }) {
    if (loading || !usage) {
        return (
            <div className="bg-[#252526] border border-[#3c3c3c] rounded-xl p-5 shadow-sm animate-pulse">
                <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                    <Activity size={16} className="text-[#808080]" />
                    Usage & Limits
                </h2>
                <div className="space-y-4 divide-y divide-[#3c3c3c]">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="pt-4 first:pt-0">
                            <div className="flex justify-between items-center mb-2">
                                <div className="h-4 w-24 bg-[#3c3c3c] rounded"></div>
                                <div className="h-4 w-12 bg-[#3c3c3c] rounded"></div>
                            </div>
                            <div className="h-1.5 w-full bg-[#3c3c3c] rounded-full"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#252526] border border-[#3c3c3c] rounded-xl p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <Activity size={16} className="text-blue-400" />
                Usage & Limits
            </h2>
            <div className="space-y-1 divide-y divide-[#3c3c3c]">
                <MeterRow
                    title="Contracts"
                    icon={Database}
                    used={usage.contracts.used}
                    limit={usage.contracts.limit}
                />
                <MeterRow
                    title="Team Members"
                    icon={Users}
                    used={usage.users.used}
                    limit={usage.users.limit}
                />
                <MeterRow
                    title="Comparisons"
                    icon={Activity}
                    used={usage.comparisons_this_month.used}
                    limit={usage.comparisons_this_month.limit}
                    suffix="/mo"
                />
            </div>
        </div>
    );
}
